import { IID } from '../models/project.model';
import { RequireProperties } from '../models/type-helpers.model';
import { v4 } from 'uuid';

/**
 * Options how to regenerate ids
 */
export interface IRegenerateIdsOptions {
  elementIds?: boolean;

  isRoot?: boolean;

  /**
   * If true it will not regenerate the root factory id when {@link IRegenerateIdsOptions.isRoot}
   * is set (e.g. outer flow pre-generated the id and children should still follow `elementIds`).
   */
  preserveRootElementIds?: boolean;

  /**
   * Source version id whose {@link Baseline.resultsRecord} / proposal rows should be
   * remapped onto the new root id. Needed when {@link preserveRootElementIds} is set and the
   * factory `id` is already the pre-assigned new id (useAddVersion / VersionAdd), so leftover
   * results keys cannot be mistaken for the version-level row.
   */
  sourceRootId?: string;
}

export type RegenerateIds = boolean | IRegenerateIdsOptions;

/**
 * Get existing it or create a new one
 * @param obj
 * @param regenerateId Force to regenerate id
 * @returns
 */
export const createId = (
  obj: Partial<IID> | IID['id'],
  regenerateId?: RegenerateIds,
): string => {
  const id = typeof obj === 'string' ? obj : obj.id;
  return !id || shouldRegenerateIds(regenerateId) ? v4() : id;
};

/**
 * Make sure id always exist
 * @param defaults
 * @param regenerateId
 * @returns
 */
export const applyId = <T extends Partial<IID>>(
  defaults: T,
  regenerateId: boolean,
): RequireProperties<T, 'id'> => {
  return {
    ...defaults,
    id: createId(defaults, regenerateId),
  };
};

/**
 * Get the regenerate ids options for child elements with changes made regarding regeration of root ids
 * @param regenerateIds
 * @returns
 */
export const getChildRegenerateIdsOptions = (
  regenerateIds?: RegenerateIds,
): RegenerateIds =>
  applyRegenerateIdsOptions(regenerateIds, {
    isRoot: false,
  });

export const applyRegenerateIdsOptions = (
  baseOptions: RegenerateIds | undefined,
  overrideOptions: Partial<IRegenerateIdsOptions> = {},
): IRegenerateIdsOptions => {
  const base =
    typeof baseOptions === 'boolean'
      ? { elementIds: baseOptions }
      : baseOptions;

  return {
    ...base,
    ...overrideOptions,
  };
};
/**
 * Should ids be regenerated or not?
 * @param regenerateIds Options for id regeneration
 * @returns Whether to assign a new id
 */
export const shouldRegenerateIds = (regenerateIds?: RegenerateIds): boolean => {
  const {
    elementIds = true,
    preserveRootElementIds = false,
    isRoot = false,
  } = applyRegenerateIdsOptions(regenerateIds);

  // Root opt-out only short-circuits when the caller explicitly requested it. Otherwise the
  // root id follows `elementIds` like descendants — important so `regenerateIds: false`
  // keeps every id intact (e.g. moves and re-parenting).
  if (isRoot && preserveRootElementIds) return false;
  return elementIds;
};

/**
 * Source version id to remap resultsRecord against when the factory root id is already
 * the pre-assigned new id. See {@link IRegenerateIdsOptions.sourceRootId}.
 */
export const getSourceRootId = (
  regenerateIds?: RegenerateIds,
): string | undefined => {
  if (!regenerateIds || typeof regenerateIds === 'boolean') return undefined;
  return regenerateIds.sourceRootId;
};
